/**
 * PhotoGallery — thumbnail strip for evidence photos attached to an entity.
 * Reads via get_photos_for_entity RPC. Generates one-hour signed URLs for
 * each thumbnail. Click opens PhotoLightbox with prev/next navigation.
 *
 * Refresh: parent passes a `refreshKey` (incremented after upload) to force
 * reload. We also expose `onRefresh` via ref pattern would be heavier — the
 * key increment is the simplest invalidation trigger.
 */

import { useEffect, useState, type JSX } from 'react';
import { ImageOff } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import PhotoLightbox from './PhotoLightbox';

interface PhotoRow {
  id: string;
  storage_path: string;
  file_size: number | null;
  mime_type: string | null;
  caption: string | null;
  uploaded_by: string | null;
  uploaded_by_name: string | null;
  uploaded_at: string;
}

export interface PhotoWithUrl extends PhotoRow {
  signed_url: string;
}

interface PhotoGalleryProps {
  entityType: 'receiving_line_item' | 'discrepancy_resolution';
  entityId: string;
  refreshKey?: number;
  readOnly?: boolean;
}

export default function PhotoGallery({
  entityType,
  entityId,
  refreshKey = 0,
  readOnly = false,
}: PhotoGalleryProps): JSX.Element {
  const [photos, setPhotos] = useState<PhotoWithUrl[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeIndex, setActiveIndex] = useState<number | null>(null);

  useEffect(() => {
    let cancelled = false;
    async function load(): Promise<void> {
      setLoading(true);
      const { data, error } = await supabase.rpc('get_photos_for_entity', {
        p_entity_type: entityType,
        p_entity_id: entityId,
      });
      if (cancelled) return;
      if (error || !data) {
        setPhotos([]);
        setLoading(false);
        return;
      }
      const rows = data as PhotoRow[];
      const signed = await Promise.all(
        rows.map(async (row) => {
          const { data: urlData } = await supabase.storage
            .from('arbi-photos')
            .createSignedUrl(row.storage_path, 3600);
          return { ...row, signed_url: urlData?.signedUrl ?? '' };
        })
      );
      if (cancelled) return;
      setPhotos(signed);
      setLoading(false);
    }
    void load();
    return () => {
      cancelled = true;
    };
  }, [entityType, entityId, refreshKey]);

  if (loading) {
    return <div className="text-xs text-ink-muted">Loading photos…</div>;
  }

  if (photos.length === 0) {
    return (
      <div className="flex items-center gap-2 text-xs text-ink-muted">
        <ImageOff size={14} />
        <span>{readOnly ? 'No photos' : 'No photos yet'}</span>
      </div>
    );
  }

  return (
    <>
      <div className="flex flex-wrap gap-2">
        {photos.map((p, i) => (
          <button
            key={p.id}
            type="button"
            onClick={() => setActiveIndex(i)}
            className="w-20 h-20 rounded-md overflow-hidden border border-border bg-surface-2 hover:border-accent transition-colors"
            aria-label={`View photo ${i + 1} of ${photos.length}`}
          >
            {p.signed_url ? (
              <img
                src={p.signed_url}
                alt={p.caption || `Photo ${i + 1}`}
                className="w-full h-full object-cover"
                loading="lazy"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center">
                <ImageOff size={20} className="text-ink-muted" />
              </div>
            )}
          </button>
        ))}
      </div>
      {activeIndex !== null && (
        <PhotoLightbox
          photos={photos}
          activeIndex={activeIndex}
          onClose={() => setActiveIndex(null)}
          onNavigate={setActiveIndex}
        />
      )}
    </>
  );
}
